import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { AWS_BASE_URL, IMAGE_URL } from '../../utils/Constants';
import Swal from 'sweetalert2';
import moment from 'moment';
import PermissionsGate from '../../utils/permissionGate';
const SignaturePadComponent = ({ disabled, signaturePadRef, formik, setShowIp, showIp, setIsSig, readOnly, isCancel, readOnlyData, is_Signature, setIs_Signature, setIpAddress, setCDate, studentSubmissionDetails }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isEmpty, setIsEmpty] = useState(true);
    const [erasorDisplay, setErasorDisplay] = useState(true);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [tool, setTool] = useState('pencil');
    const [isDisabled, setIsDisabled] = useState(false);

    // Function to resize canvas
    const resizeCanvas = () => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        // Store the signature data before resizing
        const data = signaturePadRef.current?.toData();

        // Set canvas width to match container width
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = container.offsetWidth;
        canvas.height = 200; // Keep fixed height or adjust as needed
        canvas.style.width = `${container.offsetWidth}px`;
        canvas.style.height = '200px';

        // Scale canvas for better resolution on high DPI displays
        const context = canvas.getContext('2d');
        // context.scale(ratio, ratio);

        // Restore the signature data after resizing
        if (signaturePadRef.current && data) {
            signaturePadRef.current.fromData(data);
        }
    };

    useEffect(() => {
        if (is_Signature) {
            setShowIp(true);
        }
    }, [is_Signature]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const signaturePad = new SignaturePad(canvas, {
                backgroundColor: 'rgb(255, 255, 255)',
                penColor: 'rgb(0, 0, 0)'
            });
            signaturePadRef.current = signaturePad;

            // Initial resize
            resizeCanvas();

            // Add resize event listener
            window.addEventListener('resize', resizeCanvas);

            const checkEmpty = () => {
                if (signaturePadRef.current) {
                    const currentData = signaturePad.toData();
                    setUndoStack(currentData);
                    setRedoStack([]); // Clear redo stack when new stroke is added
                    setIsEmpty(signaturePad.isEmpty());
                }
            };
            signaturePad.addEventListener('endStroke', checkEmpty);

            return () => {
                window.removeEventListener('resize', resizeCanvas);
                signaturePad.removeEventListener('endStroke', checkEmpty);
                if (signaturePadRef.current) {
                    signaturePadRef.current.clear();
                }
                const canvas = canvasRef.current;
                if (canvas && canvas.parentElement) {
                    const existingOverlay = canvas.parentElement.querySelector('.signature-overlay');
                    if (existingOverlay) {
                        canvas.parentElement.removeChild(existingOverlay);
                    }
                }
            };
        }
    }, [is_Signature]);

    useEffect(() => {
        if (signaturePadRef.current) {
            if (isDisabled) {
                signaturePadRef.current.off();
                const canvas = canvasRef.current;
                const overlay = document.createElement('div');
                overlay.style.position = 'absolute';
                overlay.style.top = '0';
                overlay.style.left = '0';
                overlay.style.right = '40px';
                overlay.style.bottom = '40px';
                overlay.style.cursor = 'not-allowed';
                overlay.style.zIndex = '1000';
                overlay.className = 'signature-overlay';
                canvas.parentElement.style.position = 'relative';
                canvas.parentElement.appendChild(overlay);

                return () => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.parentElement) {
                        const existingOverlay = canvas.parentElement.querySelector('.signature-overlay');
                        if (existingOverlay) {
                            canvas.parentElement.removeChild(existingOverlay);
                        }
                    }
                };
            } else {
                signaturePadRef.current.on();
                const canvas = canvasRef.current;
                const existingOverlay = canvas?.parentElement.querySelector('.signature-overlay');
                if (existingOverlay) {
                    canvas.parentElement.removeChild(existingOverlay);
                }
            }
        }
    }, [isDisabled]);

    useEffect(() => {
        if (isCancel) {
            handleCancel();
        }
    }, [isCancel]);

    const safeClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
            setIsEmpty(true);
            setUndoStack([]);
            setRedoStack([]);
        }
    };

    const base64ToFile = (dataUrl) => {
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], `signature.${mime.split('/')[1]}`, { type: mime });
    };

    const saveSignature = () => {
        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
            const dataURL = signaturePadRef.current.toDataURL('image/png', 1.0);
            const binary = base64ToFile(dataURL);

            setShowIp(true);
            setIsDisabled(true);
            setIsSig(binary);

            formik.setFieldValue('lecturer_Signature', binary);
            formik.setFieldValue('signature_type', 0);
            formik.setFieldValue('isDeleted', 0);
    
            fetch('https://api.ipify.org?format=json')
                .then(response => response.json())
                .then(data => {
                    setIpAddress(data.ip);
                    formik.setFieldValue('signature_ip', data.ip);

                    const currentDate = moment(new Date().getTime()).format('DD MMM YYYY, HH:mm');
                    setCDate(currentDate);
                    formik.setFieldValue('signature_date', currentDate);

                    formik.handleSubmit();
                })
                .catch(error => {
                    console.error('Error fetching IP address:', error);

                    Swal.fire({
                        title: "Error",
                        text: "Failed to save signature. Please try again.",
                        icon: "error"
                    });
                });
        }
    };

    const handleCancel = () => {
        setShowIp(false);
        setIsDisabled(false);
        formik.setFieldValue('lecturer_Signature', "");
        formik.setFieldValue('signature_ip', "");
        formik.setFieldValue('signature_date', "");
        formik.setFieldValue('signature_type', 0);
        formik.setFieldValue('isDeleted', 1);
        setIsSig(null);
        setIs_Signature(false);

        safeClearSignature();

        formik.handleSubmit();
        setIsEmpty(true);
        setErasorDisplay(true);
        setTool('pencil');
    };

    const erase = () => {
        signaturePadRef.current.penColor = 'rgb(255, 255, 255)';
        setErasorDisplay(false);
        setTool('eraser');
    };

    const draw = () => {
        signaturePadRef.current.penColor = 'rgb(0, 0, 0)';
        setErasorDisplay(true);
        setTool('pencil');
    };

    const undo = () => {
        if (signaturePadRef.current) {
            setShowIp(false);
            const data = signaturePadRef.current.toData();
            if (data.length > 0) {
                const lastStroke = data[data.length - 1];
                const newData = data.slice(0, -1);

                signaturePadRef.current.clear();
                signaturePadRef.current.fromData(newData);

                setUndoStack(newData);
                setRedoStack(prevStack => [...prevStack, lastStroke]);
                setIsEmpty(signaturePadRef.current.isEmpty());
            }
        }
    };

    const redo = () => {
        if (signaturePadRef.current && redoStack.length > 0) {
            const strokeToRedo = redoStack[redoStack.length - 1];
            const currentData = signaturePadRef.current.toData();
            const newData = [...currentData, strokeToRedo];

            signaturePadRef.current.clear();
            signaturePadRef.current.fromData(newData);

            setUndoStack(newData);
            setRedoStack(prevStack => prevStack.slice(0, -1));
            setIsEmpty(false);
        }
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!is_Signature) {
            if (isDisabled) {
                canvas.style.cursor = 'not-allowed';
            } else {
                if (tool === 'pencil') {
                    canvas.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cline x1=\'18\' y1=\'2\' x2=\'22\' y2=\'6\'%3E%3C/line%3E%3Cpath d=\'M7.5 20.5 19 9l-4-4L3.5 16.5 2 22z\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
                } else if (tool === 'eraser') {
                    canvas.style.cursor = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'24\' height=\'24\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpath d=\'m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21\'%3E%3C/path%3E%3Cpath d=\'M22 21H7\'%3E%3C/path%3E%3Cpath d=\'m5 11 9 9\'%3E%3C/path%3E%3C/svg%3E") 0 24, auto';
                }
            }
        }
    }, [tool, isDisabled, is_Signature]);

    useEffect(()=>{
        if (signaturePadRef.current?._isEmpty) {
            signaturePadRef.current.penColor = 'rgb(0, 0, 0)';

           setTool('pencil');
           setErasorDisplay(true);
        }
    })
    return (
        <div className="max-w-md position-relative signature-pad-outer">
            <div className="px-md-30 border border-gray-300 rounded-lg overflow-hidden" ref={containerRef}>
                {
                    is_Signature ? (
                        <img
                            src={
                                readOnlyData?.signature?.startsWith("https://")
                                    ? readOnlyData?.signature
                                    : readOnlyData?.fileUploadedFrom == '1'
                                        ? `${AWS_BASE_URL}${readOnlyData?.signature}`
                                        : `${IMAGE_URL}/${readOnlyData?.signature
                                            ?.replace("/home/myaie/public_html/", "")
                                            ?.replace("public/", "")}`
                            }
                            alt={
                                readOnlyData?.signature
                                    ?.replace("/home/myaie/public_html/", "")
                                    ?.replace("public/", "")
                            }
                            style={{ width: '100%', height: '200px', cursor: 'not-allowed' }}
                        />
                    ) : (
                        <PermissionsGate scopes={studentSubmissionDetails?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                            <canvas
                                ref={canvasRef}
                                style={{ width: '100%', height: '200px', padding: '30px 15px' }}
                            />
                        </PermissionsGate>
                    )
                }

                <hr className='mt-0' />
                <div className="my-2 left-0 right-0 text-center position-relative text-gray-400">
                    Sign Above
                </div>
                <div className={`position-absolute bg-white p-1 d-flex`} style={{ top: '10px', right: '10px', gap: '10px' }}>
                    <i role='button' type='button' onClick={undo} className={`fal fa-undo ${showIp ? 'd-none' : 'd-flex'}`}></i>
                    <i role='button' type='button' onClick={redo} className={`fal fa-redo ${showIp ? 'd-none' : 'd-flex'}`}></i>
                    {erasorDisplay ?
                        <i role='button' type='button' disabled={isEmpty} onClick={()=>!isEmpty ? erase() : null}  className={`fal fa-eraser ${showIp ? 'd-none' : 'd-flex'} ${isEmpty && 'icon-disabled'}`}></i>
                        :
                        <i role='button' type='button' className={`fal fa-pencil ${showIp ? 'd-none' : 'd-flex'}`} onClick={draw}></i>
                    }
                </div>
            </div>
            <div className={`sig-btns ${readOnly ? 'd-none' : 'd-flex'} my-3 flex-column flex-sm-row gap-2`}>
                <PermissionsGate scopes={studentSubmissionDetails?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                    <button className='btn btn-primary mr-3' disabled={showIp || isEmpty || disabled} onClick={saveSignature} type='button' title={"Save Signature"}>Save Signature</button>
                </PermissionsGate>
                <PermissionsGate scopes={studentSubmissionDetails?.isHybrid ? ["cassgradeassedit"] : ["overidersmart"]} errorProps={{ disabled: true }}>
                    <button className={`btn btn-danger`} style={{ cursor: (isEmpty && !showIp) ? 'not-allowed' : 'pointer' }} onClick={handleCancel} disabled={(isEmpty && !showIp) || disabled} type='button' title={"Remove Signature"}>Remove Signature</button>
                </PermissionsGate>
            </div>
        </div>
    );
};

export default React.memo(SignaturePadComponent);